import {createTheme} from '@mui/material/styles';
import {font, colors, themeColors, spacing, borders, shadows, colorPalette} from './themeConstants';

const theme = createTheme({
    palette: {
        mode: 'light',
        primary: themeColors.primary,
        secondary: themeColors.secondary,
        error: themeColors.error,
        warning: themeColors.warning,
        info: themeColors.info,
        success: themeColors.success,
        background: themeColors.background,
        text: themeColors.text,
        divider: themeColors.divider.base,
    },

    shape: {
        borderRadius: spacing.radius.fields,
    },

    typography: {
        fontFamily: font.family.main,
        button:{
            fontWeight: font.weight.bold,
            fontSize: font.size.compact,
            lineHeight: font.size.header,
            letterSpacing: font.spacing.trackingNormal,
            textTransform: 'none',
        },
        h1:{
            fontWeight: font.weight.extrabold,
            fontSize: font.size.large,
            color: colorPalette.navyViolet.navy_900,
        },
        h2:{
            fontWeight: font.weight.bold,
            fontSize: font.size.header,
            color: colorPalette.navyViolet.navy_900,
        },
        h3:{
            fontWeight: font.weight.extrabold,
            fontSize: font.size.regular,
            color: themeColors.primary.main,
        },
        h4:{
            fontWeight: font.weight.bold,
            fontSize: font.size.regular,
            lineHeight: font.size.title,
            color: colorPalette.navyViolet.navy_900,
        },
        h5:{
            fontWeight: font.weight.medium,
            fontSize: font.size.compact,
            lineHeight: font.size.regular,
        },
        title:{
            fontWeight: font.weight.bold,
            fontSize: font.size.large,
            lineHeight: font.size.extralarge,
            letterSpacing: font.spacing.trackingTightest,
            color: colors.text.primary,
        },
        titleComplement:{
            fontWeight: font.weight.regular,
            fontSize: font.size.title,
            lineHeight: font.size.extralarge,
            letterSpacing: font.spacing.trackingTightest,
            color: colors.text.black,
        },
        subtitle1:{
            fontFamily: font.family.main,
            fontWeight: font.weight.bold,
            fontSize: font.size.small,
            color: colors.text.primary,
        },
        subtitle2:{
            fontWeight: font.weight.medium,
            fontSize: font.size.regular,
            color: themeColors.secondary.main,
        },
        caption:{
            fontFamily: font.family.main,
            fontWeight: font.weight.medium,
            fontSize: font.size.compact,
            lineHeight: 1.5,
            color: colors.text.secondary,
            marginBottom: '0.5rem',
            display: 'block',
        },
        label: {
            fontFamily: font.family.main,
            fontWeight: font.weight.bold,
            fontSize: font.size.badge,
            letterSpacing: font.spacing.trackingWideFormField,
            color: colors.text.secondary,
            textTransform: 'uppercase',
        },
        informationText: {
            fontFamily: font.family.main,
            fontWeight: font.weight.regular,
            fontSize: font.size.regular,
            letterSpacing: font.spacing.trackingNormal,
            color: colors.text.detail,
        },
        dataLabel: {
            fontFamily: font.family.main,
            fontWeight: font.weight.medium,
            fontSize: font.size.small,
            letterSpacing: font.spacing.trackingWideMeta,
            color: colors.text.secondary,
            textTransform: 'uppercase',
        },
        dataValue: {
            fontFamily: font.family.main,
            fontWeight: font.weight.medium,
            fontSize: font.size.regular,
            letterSpacing: font.spacing.trackingNormal,
            color: colors.text.black,
        },
        modalTitle: {
            fontWeight: font.weight.bold,
            fontSize: font.size.large,
            lineHeight: font.size.extralarge,
            letterSpacing: font.spacing.trackingTightKpi,
            color: colors.primary.main2,
            display: 'flex',
            flexDirection: 'column',
        },
        modalSubtitle: {
            fontWeight: font.weight.bold,
            fontSize: font.size.title,
            lineHeight: font.size.ultraLarge,
            letterSpacing: font.spacing.trackingNormal,
            color: colors.text.detail,
        },
        tableHeader: {
            fontWeight: font.weight.bold,
            fontSize: font.size.compact,
            lineHeight: font.size.header,
            letterSpacing: font.spacing.trackingNormal,
            color: themeColors.table.headerText,
        },
        tableBody: {
            fontFamily: font.family.main,
            fontWeight: font.weight.medium,
            fontSize: font.size.compact,
            fontHeight: font.size.header,
            letterSpacing: font.spacing.trackingNormal,
            color: themeColors.table.bodyText,
        },
        tableBodyWide: {
            fontFamily: font.family.main,
            fontWeight: font.weight.bold,
            fontSize: font.size.regular,
            fontHeight: font.size.large,
            letterSpacing: font.spacing.trackingNormal,
            color: themeColors.table.bodyText,
        },
        timelineTitle:{
            fontFamily: font.family.main,
            fontWeight: font.weight.medium,
            fontSize: font.size.regular,
            fontHeight: font.size.title,
            letterSpacing: font.spacing.trackingNormal,
            color: themeColors.primary.main,
        },
        timelineContent:{
            fontFamily: font.family.main,
            fontWeight: font.weight.regular,
            fontSize: font.size.regular,
            fontHeight: font.size.title,
            letterSpacing: font.spacing.trackingNormal,
            color: colors.text.timeline,
        },
        timelineTime:{
            fontFamily: font.family.main,
            fontWeight: font.weight.medium,
            fontSize: font.size.small,
            fontHeight: font.size.link,
            letterSpacing: font.spacing.trackingWideMeta,
            color: colors.text.timelineTime,
            textTransform: 'uppercase',
        },
        timelineOrigin:{
            fontFamily: font.family.main,
            fontWeight: font.weight.bold,
            fontSize: font.size.small,
            fontHeight: font.size.link,
            letterSpacing: font.spacing.trackingWideMeta,
            color: colors.text.detail,
            textTransform: 'uppercase',
        },
        dropDown:{
            fontFamily: font.family.main,
            fontWeight: font.weight.medium,
            fontSize: font.size.header,
            fontHeight: font.size.title,
            letterSpacing: font.spacing.trackingNormal,
        },
        dropDownChild:{
            fontFamily: font.family.main,
            fontWeight: font.weight.regular,
            fontSize: font.size.header,
            fontHeight: font.size.title,
            letterSpacing: font.spacing.trackingNormal,
        },
        cardTitle:{
            fontFamily: font.family.main,
            fontWeight: font.weight.bold,
            fontSize: font.size.smalltitle,
            fontHeight: font.size.largeIcon,
            letterSpacing: font.spacing.trackingNormal,
            color: themeColors.primary.main,
        },
        smallCardTitle:{
            fontFamily: font.family.main,
            fontWeight: font.weight.medium,
            fontSize: font.size.regular,
            fontHeight: font.size.header,
            letterSpacing: font.spacing.trackingNormal,
            color: colors.text.black,
        },
        smallCardAltTitle:{
            fontFamily: font.family.main,
            fontWeight: font.weight.bold,
            fontSize: font.size.compact,
            fontHeight: font.size.link,
            letterSpacing: font.spacing.trackingWideBreadcrumb,
            color: colors.primary.main2,
        },
        smallCardContent:{
            fontFamily: font.family.main,
            fontWeight: font.weight.medium,
            fontSize: font.size.small,
            fontHeight: font.size.regular,
            letterSpacing: font.spacing.trackingNormal,
            color: themeColors.primary.main,
            textTransform: 'uppercase',
        },
        cardIcon:{
            fontSize: font.size.title,
            color: themeColors.icon.titleColor,
            marginRight: spacing.margin.medium,
        },
        popoverTitle:{
            fontFamily: font.family.main,
            fontWeight: font.weight.bold,
            fontSize: font.size.regular,
            fontHeight: font.size.header,
            letterSpacing: font.spacing.trackingNormal,
            color: colors.text.popoverTitle,
        },
        popoverText:{
            fontFamily: font.family.main,
            fontWeight: font.weight.regular,
            fontSize: font.size.small,
            fontHeight: font.size.title,
            letterSpacing: font.spacing.trackingNormal,
            color: colors.text.black,
        },
        bigNumberTitle:{
            fontFamily: font.family.main,
            fontWeight: font.weight.bold,
            fontSize: font.size.small,
            fontHeight: font.size.link,
            maxHeight: font.size.link,
            letterSpacing: font.spacing.trackingNormal,
            textTransform: 'uppercase',
        },
        bigNumberValue:{
            fontFamily: font.family.main,
            fontWeight: font.weight.bold,
            fontSize: font.size.large,
            lineHeight: font.size.title,
            letterSpacing: font.spacing.trackingNormal,
            height: font.size.large,
            display: 'flex',
            alignItems: 'flex-end',
        },
        breadcrumb: {
            fontFamily: font.family.main,
            fontWeight: font.weight.bold,
            fontSize: font.size.small,
            fontHeight: font.size.regular,
            letterSpacing: font.spacing.trackingWideBreadcrumb,
            // color: themeColors.breadcrumb.base, // Géré dans ./themeConstants/objects/breadcrumbUnderline
        },
        breadcrumbCurrent: {
            fontFamily: font.family.main,
            fontWeight: font.weight.bold,
            fontSize: font.size.small,
            fontHeight: font.size.regular,
            letterSpacing: font.spacing.trackingWideBreadcrumb,
            color: themeColors.breadcrumb.hover,
        },
    },

    components: {
        MuiCssBaseline: {
            styleOverrides: {
                '*:focus, *:focus-visible': {
                    boxShadow: shadows.globalFocusShadow,
                    outline: 'none',
                },
            },
        },
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    title: 'h3',
                },
            },
            styleOverrides: {
                breadcrumb: {
                    maxWidth: '8rem',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    display: 'inline-block',
                    verticalAlign: 'sub',
                },
                breadcrumbCurrent: {
                    maxWidth: '8rem',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    display: 'inline-block',
                    verticalAlign: 'sub',
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                variant: 'outlined'
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontWeight: font.weight.bold,
                    border: 'solid 1px',
                    textTransform: 'none',
                    padding: '6px 12px',
                    boxShadow: '0px 2px 4px 0px #00000040',
                    '&:hover': {
                        boxShadow: 'initial',
                    },
                },
                endIcon:{
                    marginLeft: spacing.margin.small,
                    '&>*:nth-of-type(1)':{
                        fontSize:'inherit',
                    },
                },

                sizeSmall: {
                    padding: '8px 4px',
                    fontSize: font.size.small,
                    height: '24px',
                },

                sizeMedium: {
                    padding: '12px 6px',
                    fontSize: font.size.compact,
                    height: '36px',
                },

                sizeLarge: {
                    padding: '10px 12px',
                    fontSize: font.size.regular,
                    height: '46px',
                },

                startIcon: {
                    marginLeft: '0',
                    marginRight: '0.25rem',
                },

                iconSizeSmall: {
                    '& > *:first-of-type': {
                        fontSize: font.size.small,
                    },
                },

                iconSizeMedium: {
                    '& > *:first-of-type': {
                        fontSize: font.size.compact,
                    },
                },

                iconSizeLarge: {
                    '& > *:first-of-type': {
                        fontSize: font.size.regular,
                    },
                },

                outlined: {
                    '&:hover': {
                        boxShadow: 'initial',
                    },
                    '&:focus, &:active': {
                        boxShadow: shadows.globalFocusShadow,
                    },
                },

                contained: {
                    '&:hover': {
                        boxShadow: 'initial',
                    },
                    '&:focus, &:active': {
                        boxShadow: shadows.globalFocusShadow,
                    },
                },
            },
        },
        MuiButtonBase: {
            styleOverrides: {
                root: {
                    '&.MuiPickersDay-root': {
                        borderRadius: '0',
                    },
                    '&.MuiAccordionSummary-root':{
                        borderRadius: spacing.radius.item,
                    },
                },
            },
            defaultProps: {
                disableRipple: true,
                disableTouchRipple: true,
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    padding: '6px',
                    borderRadius: spacing.radius.fields,
                    fontSize: '1.2rem',
                },
                colorPrimary: {
                    backgroundColor: colors.secondary.main1,
                    color: colors.secondary.main3,
                    '&:hover': {
                        backgroundColor: colors.primary.main3,
                        color: colors.primary.main1,
                    },
                },
                colorSecondary: {
                    backgroundColor: colors.primary.main1,
                    color: colors.text.btnPrimary,
                    '&:hover': {
                        backgroundColor: colors.primary.main3,
                        color: colors.primary.main1,
                    },
                },
                colorSuccess: {
                    borderColor: colors.success.main1,
                    color: colors.success.main1,
                    '&:hover': {
                        backgroundColor: colors.success.main1,
                    },
                },
                colorError: {
                    borderColor: colors.error.main1,
                    color: colors.error.main1,
                    '&:hover': {
                        backgroundColor: colors.error.light,
                    },
                },
                sizeSmall: {
                    padding: '4px',
                    fontSize: '1rem',
                },
                sizeMedium: {
                    padding: '6px',
                    fontSize: '1.2rem',
                },
                sizeLarge: {
                    padding: '10px',
                    fontSize: '1.5rem',
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    fontFamily: font.family.main,
                    lineHeight: '24px',
                    color: colors.text.primary,
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    marginBottom: '1rem',
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    marginLeft: 0,
                    marginRight: 0,
                },
            },
        },
        MuiRadio: {
            defaultProps: {
                color: 'primary'
            },
            styleOverrides: {
                root: {
                    color: '#21252960',
                    padding: '1px',
                    '& .MuiSvgIcon-root': {
                        borderRadius: '50%',
                    },
                    '&.Mui-focusVisible': {
                        '& .MuiSvgIcon-root': {
                            boxShadow: 'inset 0 0 0 10px rgba(149, 30, 73, 0.1)',
                            outline: '1px solid rgba(149, 30, 73, 0.1)',
                        },
                    },
                },
            },
        },
        MuiSwitch: {
            defaultProps: {
                color: 'primary'
            },
            styleOverrides: {
                root: {
                    width: '3.3rem',
                    height: '30px',
                    padding: '3px',
                },
                thumb: {
                    width: '18px',
                    height: '18px',
                    boxShadow: 'none',
                    borderRadius: '30%',
                    margin: '0',
                    '&:focus': {
                        // boxShadow: '0 0 0 0.2rem rgba(149, 30, 73, 0.25)',
                        boxShadow: 'none',
                    },
                },
                switchBase: {
                    top: 'auto',
                    left: '0.25rem',
                    padding: '3px',
                    '&.Mui-focusVisible': {
                        '& .MuiSwitch-thumb': {
                            // outline: '0.2rem solid rgba(149, 30, 73, 0.5)',
                            // outline: `0.2rem solid ${colors.primary.light}`
                            outline: shadows.globalFocusOutline,
                        },
                    },
                    '&:active': {
                        '& .MuiSwitch-thumb': {
                            outline: '0.2rem solid rgba(149, 30, 73, 0.25)',
                            borderColor: colors.primary.main1,
                            border: 'solid 1px',
                        },
                    },
                    '&:hover': {
                        backgroundColor: 'unset',
                    },
                },
            },
        },
        MuiCheckbox: {
            defaultProps: {
                color: 'primary'
            },
            styleOverrides: {
                root: {
                    padding: '0',
                    color: colors.text.secondary,
                    '&:focus': {
                        backgroundColor: colors.primary.light,
                    },
                    '&.Mui-focusVisible': {
                        borderRadius: '10%',
                        color: '#951e4a',
                        backgroundColor: colors.primary.light,
                        outlineOffset: 2,
                    }
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    padding: 0,
                    position: 'relative',
                    fontSize: 16,
                    width: 'auto',
                    transition: 'border-color 0.3s, background-color 0.3s, box-shadow 0.3s',
                },
                input: {
                    borderRadius: spacing.radius.fields,
                    '&:invalid': {
                        boxShadow: 0,
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: spacing.radius.fields,
                    padding: '0 !important',
                    '&.Mui-focused input:invalid + .MuiOutlinedInput-notchedOutline': {
                        boxShadow: shadows.globalFocusShadow,
                    },
                    '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1px',
                    },
                    '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
                        borderColor: colors.borders.active,
                    },
                    '&:hover:not(.Mui-focused):not(.Mui-error):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
                        borderColor: colors.borders.hover,
                    },
                },
                input: {
                    padding: spacing.padding.input,
                },
                adornedEnd: {
                    justifyContent: 'end',
                },
                inputAdornedEnd: {
                    paddingRight: undefined,
                },
                notchedOutline: {
                    borderColor: `${colors.borders.light}`,
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    position: 'absolute',
                    height: 'auto',
                    right: '0.5em',
                    width: '1.3em',
                    '& .MuiIconButton-root': {
                        padding: '0',
                        marginRight: '0.5rem',
                    },
                },
                positionEnd: {
                    marginLeft: 0,
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                inputRoot: {
                    '&[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input:first-child': {
                        padding: '10px 3.5rem 10px 12px !important',
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontWeight: font.weight.medium,
                    height: '29px',
                    padding: '6px 12px',
                    fontSize: font.size.compact,
                    borderRadius: spacing.radius.item,
                },
                label: {
                    paddingLeft: '0px',
                    paddingRight: '0px',
                },
                icon: {
                    color: 'unset',
                    marginLeft: '0px',
                    marginRight: '0.345rem',
                }
            },
        },
        MuiMenu:{
            styleOverrides:{
                paper:{
                    borderRadius: spacing.radius.menu,
                },
            },
        },
        MuiList:{
            styleOverrides:{
                root:{
                    '&not:(MuiList-dense)':{
                        color: themeColors.primary.main,
                        backgroundColor: colors.background.menu,
                        borderRadius: spacing.radius.menu,
                    }
                },
            },
        },
        MuiListItemButton: {
            styleOverrides:{
                root:{
                    borderRadius: spacing.radius.item,
                    gap: spacing.gap.small,
                    color: colors.text.secondary,
                    '&:hover':{
                        color: colors.text.primary,
                    },
                    '&:active':{
                        fontWeight: font.weight.bold,
                        backgroundColor: colors.background.default,
                        boxShadow: `0 0 0 0.2rem ${colors.borders.hover}`,
                    },
                    '&:focus': {
                        boxShadow: `0 0 0 0.2rem ${colors.borders.hover}`,
                    },
                    '&.Mui-selected':{
                        color: colors.text.primary,
                        backgroundColor: themeColors.primary.light,
                    }
                },
            },
        },
        MuiListItemIcon:{
            styleOverrides:{
                root:{
                    minWidth: 'auto',
                },
            },
        },
        MuiMenuItem:{
            styleOverrides:{
                root:{
                    gap:spacing.gap.small,
                    borderRadius: `calc(${spacing.radius.menu} / 2)`,
                    '&:focus':{
                        boxShadow: 'none',
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    overflow: 'visible',
                    // borderBottom: '0.0625rem solid #C4C4C4',
                    fontWeight: font.weight.medium,
                    lineHeight: font.size.header,
                    padding: '0px 1.5rem',
                    minHeight: '3rem',
                    width: 'auto',
                    borderRadius: spacing.radius.fields,
                    '@media (min-width:600px)': {
                        minWidth: 'auto',
                    },
                    transition: 'background-color 120ms ease-out, box-shadow 120ms ease-out',
                    '&:active': {
                        // boxShadow: `0 0 0 0.2rem ${colors.primary.light}`,
                        color: themeColors.primary.main,
                    },
                    '&:hover': {
                        outline: '1px solid #E2E2E2',
                        outlineOffset: '-1px',
                    },
                    '&.Mui-selected': {
                        backgroundColor: colors.background.paper,
                        borderRadius: `${spacing.radius.tab} ${spacing.radius.tab} 0px 0px`,
                        padding: '0px 2.5rem',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: '100%',
                            left: '-0.5px',
                            right: '-0.5px',
                            bottom: `-${spacing.radius.container}`,
                            background: colors.background.paper,
                            zIndex: -1,
                        },

                        '&:active::before, &.Mui-focused::before': {
                            top: 'calc(100% + 4px)',
                        },
                    },
                },
                textColorPrimary: {
                    color: colors.text.secondary,
                    border: '0.8px solid transparent',
                    transition: 'all 300ms',
                    fontSize: font.size.compact,
                    fontWeight: font.weight.medium,
                    lineHeight: font.size.header,
                    letterSpacing: font.spacing.trackingTightTab,
                    textTransform: 'uppercase',
                    '&.Mui-selected': {
                        color: themeColors.primary.main,
                        fontWeight: font.weight.bold,
                        letterSpacing: font.spacing.trackingTightInactive,
                        '&:not(:active):not(:focus):not(.Mui-focusVisible)': {
                            // boxShadow: `0 1px 2px 0 ${colors.borders.hover}`,
                        }
                    },
                    '&:hover': {
                        color: themeColors.primary.main,
                        outline: 'none',
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    overflow: 'visible',
                    minHeight: 'auto',
                    // padding: `0px ${spacing.radius.container}`,
                },
                fixed: {
                    overflow: 'visible !important',
                },
                indicator: {
                    backgroundColor: colors.background.paper,
                },
            },
            defaultProps: {
                textColor: 'primary',
                indicatorColor: 'success'
            },
        },
        MuiTimelineItem:{
            styleOverrides:{
                root:{
                    width: '100%',
                },
            },
        },
        MuiTimelineDot:{
            styleOverrides:{
                root:{
                    width: spacing.size.timelineDotSize,
                    height: spacing.size.timelineDotSize,
                    border: borders.timelineBorder,
                    borderRadius: spacing.radius.timelineDot,
                    boxShadow: shadows.timelineDotShadows,
                    backgroundColor: themeColors.background.paper,
                    alignItems: 'center',
                    justifyContent: 'center',
                }
            },
        },
        MuiTimelineConnector:{
            styleOverrides:{
                root:{
                    backgroundColor: colors.borders.timeline,
                }
            },
        },
        MuiStepIcon: {
            styleOverrides: {
                root: {
                    width: '2rem',
                    height: '2rem',
                    color: '#E2E2E2',
                    '&.Mui-active .MuiStepIcon-text': {
                        fill: colors.primary.contrastText,
                    },
                    '&.Mui-completed .MuiStepIcon-text': {
                        fill: colors.primary.contrastText,
                    },
                },
                text: {
                    fill: '#20202080',
                    fontSize: font.size.compact,
                },
            },
        },
        MuiStepLabel: {
            styleOverrides: {
                iconContainer: {
                    zIndex: 1,
                },
                label: {
                    '&.MuiStepLabel-alternativeLabel': {
                        marginTop: '0px',
                    },
                },
            },
        },
        MuiStepConnector: {
            styleOverrides: {
                alternativeLabel: {
                    left: 'calc(-50% + 16px)',
                    right: 'calc(50% + 16px)',
                },
                line: {
                    borderColor: '#E2E2E2',
                },
                root: {

                    '&.Mui-active .MuiStepConnector-line, &.Mui-completed .MuiStepConnector-line': {
                        borderColor: themeColors.primary.main,
                    },
                },
                lineHorizontal: {
                    borderTopWidth: '4px',
                },
            },
        },
        MuiTable:{
            styleOverrides: {
                root: {
                    borderCollapse: 'separate',
                    borderSpacing: `0px ${spacing.padding.tableSpace}`,
                    marginTop: '-'+ spacing.padding.tableSpace,
                    // marginBottom: '-'+ spacing.padding.tableSpace,
                },
            },
        },
        MuiTableRow:{
            styleOverrides: {
                root:{
                    '&:not(.MuiTableRow-head)':{
                        backgroundColor: themeColors.background.paper,
                    },
                    '&.MuiTableRow-hover:not(.MuiTableRow-head)':{
                        borderRadius: spacing.radius.tableBody,
                        boxShadow: shadows.tableRow,
                    },
                    '&.MuiTableRow-hover:hover':{
                        backgroundColor: themeColors.table.hover,
                    },
                    '&.MuiTableRow-hover:active':{
                        backgroundColor: themeColors.table.active,
                    },
                    '&.MuiTableCell-body':{
                        paddingTop: spacing.padding.tableSpace,
                    },
                    '& .MuiTableCell-head:first-child':{
                        borderTopLeftRadius: spacing.radius.tableHeader,
                        '&.MuiTableSortLabel-root':{
                            borderTopLeftRadius: spacing.radius.tableHeader,
                        }
                    },
                    '& .MuiTableCell-head:last-child':{
                        borderTopRightRadius: spacing.radius.tableHeader,
                        '&.MuiTableSortLabel-root':{
                            borderTopLeftRadius: spacing.radius.tableHeader,
                        }
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    border: 'none',
                    paddingLeft: '15px',
                    padding: spacing.padding.tableRow,
                },
                head:{
                    // lineHeight: font.size.badge,
                    // paddingTop: `1px !important`,
                    // paddingBottom: `1px !important,
                },
                body:{
                    '&:first-child':{
                        borderTopLeftRadius: spacing.radius.tableHeader,
                        borderBottomLeftRadius: spacing.radius.tableHeader,
                    },
                    '&:last-child':{
                        borderTopRightRadius: spacing.radius.tableHeader,
                        borderBottomRightRadius: spacing.radius.tableHeader,
                    },
                },
            },
        },
        MuiTableSortLabel: {
            styleOverrides: {
                root:{
                    padding: spacing.padding.tableHeader,
                    borderRadius: spacing.radius.tableHeaderCell,
                    lineHeight: font.size.large,
                    width: '100%',
                    '&:hover': {
                        color: 'inherit',
                    },
                    '&.MuiTableSortLabel-active': {
                        color: colors.text.table,
                        '&& .MuiTableSortLabel-icon': {
                            color: 'inherit !important',
                        },
                    },
                },
            },
        },
        MuiPickersCalendarHeader: {
            styleOverrides: {
                root: {
                    marginTop: '0.75rem',
                    marginBottom: '1.5rem',
                    margin: '0.75rem',
                },
                labelContainer: {
                    overflow: 'visible'
                },
            },
        },
        MuiYearCalendar: {
            styleOverrides: {
                root: {
                    display: 'flex',
                    flexFlow: 'wrap',
                },
            },
        },
        MuiPickersYear: {
            styleOverrides: {
                root: {
                    flexBasis: '33%',
                },
                yearButton: {
                    fontFamily: font.family.main,
                    fontSize: font.size.compact,
                    fontWeight: font.weight.medium,
                    borderRadius: '0',
                    '&.Mui-selected': {
                        margin: '0.5rem auto 0.5rem 0.5rem',
                    },
                },
            },
        },
        MuiPickersSectionList: {
            styleOverrides: {
                root: {
                '&&': {
                    padding: spacing.padding.input,
                },
                },
            },
        },
        MuiPickersOutlinedInput: {
            styleOverrides: {
                root: {
                    '&.MuiPickersInputBase-root': {
                        padding: 0,
                    },
                    '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
                        borderWidth: '1px',
                        borderColor: colors.borders.active,
                    },

                    '&:hover:not(.Mui-focused):not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline': {
                        borderColor: colors.borders.hover,
                    },

                    '&.Mui-focused:not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline': {
                        borderColor: colors.borders.active,
                    },

                    '&.Mui-focused input:invalid + .MuiPickersOutlinedInput-notchedOutline': {
                        boxShadow: shadows.globalFocusShadow,
                    },
                },
                notchedOutline: {
                    borderColor: `${colors.borders.light}`,
                },
            },
        },
        MuiDayCalendar: {
            styleOverrides: {
                weekDayLabel: {
                    width: '40px',
                    height: '40px',
                    margin: '0',
                    backgroundColor: colors.background.table,
                    color: colors.text.table,
                    fontWeight: font.weight.medium,
                },
            },
        },
        MuiBreadcrumbs:{
            styleOverrides:{
                li: {
                    maxWidth: '8rem',
                    color: themeColors.breadcrumb.base,
                    '&:last-child':{
                        color: themeColors.breadcrumb.hover,
                    }
                },
                separator:{
                    color: themeColors.breadcrumb.base,
                },
            },
        },
        MuiContainer: {
            styleOverrides: {
                root: {
                    paddingLeft: '0px',
                    paddingRight: '0px',
                    padding: '0px',
                    maxWidth: 'unset',
                    borderRadius: spacing.radius.container,
                    '@media (min-width: 600px)': {
                        paddingLeft: '0px',
                        paddingRight: '0px',
                        margin: 0,
                    },
                    '&[data-layout="Main"]': {
                        maxWidth: 'unset',
                    },
                    '&[data-layout="Header"]': {
                        justifyContent: 'space-between'
                    },
                    '&[data-layout="Title"]': {
                        gap: spacing.gap.small
                    },
                    '&[data-layout="Content"]': {
                        backgroundColor: themeColors.background.paper,
                        padding: spacing.padding.content,
                        maxWidth: 'unset',
                    },
                    '&[data-layout="TabPanel"]': {
                        padding: spacing.padding.content,
                        radius: spacing.radius.timeline,
                        backgroundColor: colors.background.paper,
                    },
                    '&[data-layout="Timeline"]': {
                        padding: spacing.padding.timeline,
                        radius: spacing.radius.timeline,
                        boxShadow: shadows.timelineShadow,
                        backgroundColor: colors.background.paper,
                        border: borders.timelineBorder,
                        gap: spacing.gap.timeline,
                    },
                    '&[data-layout="ModalFrame"]':{
                        backgroundColor: themeColors.background.paper,
                        backgroundClip: 'padding-box',
                        border: '0.0625rem solid rgba(32, 32, 32, 0.2)',
                        margin: '1.75rem auto',
                        padding: '0',
                        borderRadius: spacing.radius.modal,
                        display: 'flex',
                        flexDirection: 'column',
                        outline: '0',
                        overflow: 'hidden',
                        pointerEvents: 'auto',
                        position: 'relative',
                        maxWidth: '800px',
                        width: 'auto',
                        maxHeight: '90%',
                        boxShadow: 'none',
                    }, 
                    '&[data-layout="ModalFrameXL"]': {
                        backgroundColor: themeColors.background.paper,
                        backgroundClip: 'padding-box',
                        border: '0.0625rem solid rgba(32, 32, 32, 0.2)',
                        margin: '1.75rem',
                        padding: '0',
                        borderRadius: spacing.radius.modal,
                        display: 'flex',
                        flexDirection: 'column',
                        outline: '0',
                        overflow: 'hidden',
                        pointerEvents: 'auto',
                        position: 'relative',
                        width: 'auto',
                        maxHeight: '90%',
                        boxShadow: 'none',
                    },
                    '&[data-layout="ModalHeader"]': {
                        alignItems: 'center',
                        borderBottom: `1px solid ${themeColors.divider.base}`,
                        display: 'flex',
                        flexShrink: '0',
                        // height: '4rem',
                        justifyContent: 'space-between',
                        padding: spacing.padding.modal,
                        borderRadius: '0',
                    },
                    '&[data-layout="ModalMain"]': {
                        padding: spacing.padding.modal,
                    },
                    '&[data-layout="ModalFooter"]': {
                        alignItems: 'center',
                        borderTop: `1px solid ${themeColors.divider.base}`,
                        display: 'flex',
                        gap: spacing.gap.medium,
                        justifyContent: 'space-between',
                        padding: spacing.padding.modal,
                        borderRadius: '0',
                    },
                    '&[data-layout="ActionBar"]': {
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '1rem',
                        marginTop: '1rem',
                    },
                    '&[data-layout="ModalHeaderXL"]': {
                        alignItems: 'center',
                        backgroundColor: colors.background.paper,
                        borderBottom: `1px solid ${themeColors.divider.base}`,
                        display: 'flex',
                        flexShrink: '0',
                        justifyContent: 'space-between',
                        padding: spacing.padding.modalXL,
                        borderRadius: '0',
                    },
                    '&[data-layout="ModalMainXL"]': {
                        padding: spacing.padding.modal,
                        backgroundColor: themeColors.background.default,
                        borderRadius: '0',
                    },
                    '&[data-layout="ModalFooterXL"]': {
                        alignItems: 'center',
                        backgroundColor: colors.background.paper,
                        borderTop: `1.25px solid ${themeColors.divider.base}`,
                        display: 'flex',
                        gap: spacing.gap.medium,
                        justifyContent: 'space-between',
                        padding: spacing.padding.modalXL,
                        borderRadius: '0',
                    },
                    '&[data-layout="ActionBarXL"]': {
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '1rem',
                        marginTop: '1rem',
                    },
                    '&[data-layout="Icon"]': {
                        height: spacing.size.iconDisplay,
                        width: spacing.size.iconDisplay,
                        backgroundColor: themeColors.icon.background,
                        color: themeColors.icon.color,
                        fontSize: font.size.largeIcon,
                        textAlign: 'center',
                        placeContent: 'center',
                        '&>i': {
                            paddingBottom: '10px',
                        }
                    },
                },
                maxWidthLg: {
                    '@media (min-width: 1280px)': {
                        maxWidth: 'unset'
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides:{
                root:{
                    height: 'fit-content',
                    minWidth: spacing.size.cardMinSize,
                    width: 'fit-content',
                    boxShadow: shadows.cardShadows,
                    '&.MuiPaper-root': {
                        height: 'fit-content',
                    },
                },
            },
        },
        MuiCardContent:{
            styleOverrides:{
                root:{
                    padding: spacing.padding.card,
                    display: 'flex',
                    justifyContent: 'flex-start',
                    flexDirection:'column',
                    gap: spacing.gap.card,
                    '&:last-child':{
                        paddingBottom:spacing.padding.card,
                    },
                    '&[variant="small"]':{
                        gap:'0px',
                        padding: spacing.padding.cardSmall,
                    },
                },
                
            },
        },
        MuiCardActions:{
            styleOverrides:{
                root:{
                    padding: '0px',
                    paddingBottom:'16px',
                    justifyContent: 'space-between',
                },
            },
        },
        MuiPaper:{
            styleOverrides:{
                root:{
                    '&.MuiPopover-paper':{
                        backgroundColor: themeColors.popover.background,
                        padding: spacing.padding.popover, // soit 0.625rem
                    },
                },
            },
        },
        MuiAccordion:{
            styleOverrides:{
                root:{
                    borderRadius: spacing.radius.item,
                    '&.MuiPaper-root':{
                        backgroundColor: colors.background.default,
                        border: `1px solid ${colors.borders.light}`
                    },
                    '&.Mui-expanded':{
                        margin: 0,
                    },
                    '&.heading':{
                        borderRadius: spacing.radius.item,
                    },
                    '&:first-of-type':{
                        borderRadius: spacing.radius.item,
                    },
                    '&::before':{
                        opacity: 0,
                    },
                },
            },
        },
        MuiAccordionDetails:{
            styleOverrides:{
                root:{
                    padding: spacing.padding.accordionDetail,
                    borderRadius: `0px 0px ${spacing.radius.item} ${spacing.radius.item}`,
                    background: colors.background.paper,
                }
            }
        },
        MuiAccordionSummary:{
            styleOverrides:{
                root:{
                    '&.Mui-expanded':{
                        minHeight: '48px',
                    },
                },
                content:{
                    '&.Mui-expanded':{
                        margin: '12px',
                    }
                }
            }
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    marginTop: '0.25rem',
                    marginBottom: 0,
                    marginLeft: 0,
                    marginRight: 0,
                    '&.Mui-error': {
                        fontSize: font.size.small,
                        color: colors.error.main1,
                        fontWeight: font.weight.medium,
                    },
                }
            },
        },
        MuiSvgIcon: {
            styleOverrides: {
                root: {fontSize: '1.2rem'},
            },
        },
    },
});

export default theme;